import React, { useState, useEffect } from 'react';
import { X, CreditCard, Lock, CheckCircle, AlertCircle, Loader, User as UserIcon } from 'lucide-react';
import { useI18n } from '../i18n/I18nContext';
import { plansAPI } from '../services/api';
import '../styles/PaymentModal.css';

const splitName = (full) => {
    const parts = (full || '').trim().split(/\s+/);
    return {
        firstName: parts[0] || '',
        lastName: parts.length > 1 ? parts.slice(1).join(' ') : '',
    };
};

const PaymentModal = ({ isOpen, onClose, user, plan, onPaymentSuccess }) => {
    const { t } = useI18n();
    const [loading, setLoading] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [paymentMessage, setPaymentMessage] = useState('');
    const [cardDetails, setCardDetails] = useState({
        cardHolderName: '',
        cardNumber: '',
        expireMonth: '',
        expireYear: '',
        cvc: '',
    });
    // Pre-fill from the user's saved default address (joined in /users/me).
    // The address rows themselves are immutable — submitting a different
    // address here will create a new addresses row on the next /payment/create.
    const [billing, setBilling] = useState(() => {
        const fallbackName = splitName(user?.name);
        const a = user?.default_address || {};
        return {
            firstName: a.first_name || fallbackName.firstName,
            lastName:  a.last_name  || fallbackName.lastName,
            phone:     a.phone || '',
            address:   a.address_line || '',
            city:      a.city || '',
            zipCode:   a.zip_code || '',
            country:   a.country || 'Turkey',
        };
    });

    const API_URL = '/api';
    const [plans, setPlans] = useState([]);
    const currentPlan = plans.find((p) => p.slug === plan);
    const displayPrice = currentPlan ? Number(currentPlan.price) : null;

    useEffect(() => {
        let active = true;
        plansAPI.list()
            .then((res) => { if (active) setPlans(res.data || []); })
            .catch(() => { /* fall through with empty list; submit button stays disabled until loaded */ });
        return () => { active = false; };
    }, []);

    useEffect(() => {
        const handleMessage = (event) => {
            if (event.data?.type === 'PAYMENT_COMPLETE') {
                if (event.data.status === 'success') {
                    setPaymentStatus('success');
                    setPaymentMessage(t('payment.status.successMessage'));
                    if (onPaymentSuccess) onPaymentSuccess(event.data);
                    setTimeout(onClose, 3000);
                } else {
                    setPaymentStatus('error');
                    setPaymentMessage(event.data.message || t('payment.status.verificationFailed'));
                }
            }
        };
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [onPaymentSuccess, onClose, t]);

    const formatCardNumber = (value) =>
        value.replace(/\s/g, '').replace(/(\d{4})/g, '$1 ').trim();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setPaymentStatus('processing');
        setPaymentMessage(t('payment.status.connecting'));

        try {
            const response = await fetch(`${API_URL}/payment/create`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user,
                    planName: plan,
                    paymentCard: {
                        ...cardDetails,
                        cardNumber: cardDetails.cardNumber.replace(/\s/g, ''),
                    },
                    buyer: billing,
                    use3DSecure: true,
                }),
            });

            const contentType = response.headers.get('content-type');

            if (contentType && contentType.includes('text/html')) {
                const htmlContent = await response.text();
                setPaymentStatus('3ds');
                setPaymentMessage(t('payment.status.threeDsMessage'));

                const popup = window.open('', 'iyzico3ds', 'width=600,height=700,left=400,top=100');
                if (!popup) throw new Error(t('payment.status.popupBlocked'));

                popup.document.write(htmlContent);
            } else {
                const data = await response.json();
                if (data.success) {
                    setPaymentStatus('success');
                    setPaymentMessage(t('payment.status.paymentSuccessful'));
                    if (onPaymentSuccess) onPaymentSuccess(data.data);
                } else {
                    setPaymentStatus('error');
                    setPaymentMessage(data.error || t('payment.status.cardDeclined'));
                }
            }
        } catch (err) {
            setPaymentStatus('error');
            setPaymentMessage(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const planLabel = plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : '';
    const statusHeader = paymentStatus === '3ds'
        ? t('payment.status.threeDsTitle')
        : paymentStatus === 'success'
            ? t('payment.status.successHeader')
            : paymentStatus === 'error'
                ? t('payment.status.errorHeader')
                : t('payment.status.processingHeader');

    return (
        <div className="payment-modal-overlay">
            <div className="payment-modal">
                <div className="payment-modal-header">
                    <h2>{t('payment.upgradeTo', { plan: planLabel })}</h2>
                    <button onClick={onClose} className="close-btn" disabled={loading}>
                        <X size={20} />
                    </button>
                </div>

                {!paymentStatus ? (
                    <>
                        <div className="payment-summary">
                            <div className="summary-row">
                                <span>{t('payment.summary.plan')}</span>
                                <strong>{plan}</strong>
                            </div>
                            <div className="summary-row total">
                                <span>{t('payment.summary.total')}</span>
                                <strong className="price">{displayPrice != null ? `₺${displayPrice}` : '—'}</strong>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="payment-form">
                            <div className="payment-section-label">
                                <UserIcon size={14} /> {t('payment.billingSection')}
                            </div>

                            <div className="form-row form-row-2">
                                <div className="form-group">
                                    <label>{t('payment.fields.firstName')}</label>
                                    <input
                                        required
                                        type="text"
                                        value={billing.firstName}
                                        onChange={(e) => setBilling({ ...billing, firstName: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{t('payment.fields.lastName')}</label>
                                    <input
                                        required
                                        type="text"
                                        value={billing.lastName}
                                        onChange={(e) => setBilling({ ...billing, lastName: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>{t('payment.fields.phone')}</label>
                                <input
                                    required
                                    type="tel"
                                    placeholder={t('payment.fields.phonePlaceholder')}
                                    value={billing.phone}
                                    onChange={(e) => setBilling({ ...billing, phone: e.target.value })}
                                />
                            </div>

                            <div className="form-group">
                                <label>{t('payment.fields.address')}</label>
                                <input
                                    required
                                    type="text"
                                    placeholder={t('payment.fields.addressPlaceholder')}
                                    value={billing.address}
                                    onChange={(e) => setBilling({ ...billing, address: e.target.value })}
                                />
                            </div>

                            <div className="form-row form-row-2">
                                <div className="form-group">
                                    <label>{t('payment.fields.city')}</label>
                                    <input
                                        required
                                        type="text"
                                        placeholder={t('payment.fields.cityPlaceholder')}
                                        value={billing.city}
                                        onChange={(e) => setBilling({ ...billing, city: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{t('payment.fields.postalCode')}</label>
                                    <input
                                        required
                                        type="text"
                                        placeholder={t('payment.fields.postalCodePlaceholder')}
                                        value={billing.zipCode}
                                        onChange={(e) => setBilling({ ...billing, zipCode: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="payment-section-label">
                                <CreditCard size={14} /> {t('payment.cardSection')}
                            </div>

                            <div className="form-group">
                                <label>{t('payment.fields.cardHolder')}</label>
                                <input
                                    required
                                    type="text"
                                    placeholder={t('payment.fields.cardHolderPlaceholder')}
                                    value={cardDetails.cardHolderName}
                                    onChange={(e) => setCardDetails({ ...cardDetails, cardHolderName: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>{t('payment.fields.cardNumber')}</label>
                                <div className="input-with-icon">
                                    <input
                                        required
                                        type="text"
                                        placeholder="0000 0000 0000 0000"
                                        maxLength="19"
                                        value={cardDetails.cardNumber}
                                        onChange={(e) => setCardDetails({ ...cardDetails, cardNumber: formatCardNumber(e.target.value) })}
                                    />
                                    <CreditCard className="input-icon" size={18} />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>{t('payment.fields.month')}</label>
                                    <input
                                        required
                                        type="text"
                                        placeholder="MM"
                                        maxLength="2"
                                        value={cardDetails.expireMonth}
                                        onChange={(e) => setCardDetails({ ...cardDetails, expireMonth: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{t('payment.fields.year')}</label>
                                    <input
                                        required
                                        type="text"
                                        placeholder="YY"
                                        maxLength="2"
                                        value={cardDetails.expireYear}
                                        onChange={(e) => setCardDetails({ ...cardDetails, expireYear: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{t('payment.fields.cvc')}</label>
                                    <input
                                        required
                                        type="text"
                                        placeholder="123"
                                        maxLength="4"
                                        value={cardDetails.cvc}
                                        onChange={(e) => setCardDetails({ ...cardDetails, cvc: e.target.value })}
                                    />
                                </div>
                            </div>

                            <button type="submit" className="pay-btn" disabled={loading || displayPrice == null}>
                                <Lock size={16} /> {t('payment.payBtn', { price: displayPrice ?? '' })}
                            </button>
                        </form>
                    </>
                ) : (
                    <div className="status-container">
                        {paymentStatus === 'processing' && <Loader className="spinning" size={48} />}
                        {paymentStatus === '3ds' && <Lock className="pulse" size={48} color="#6366f1" />}
                        {paymentStatus === 'success' && <CheckCircle size={48} color="#22c55e" />}
                        {paymentStatus === 'error' && <AlertCircle size={48} color="#ef4444" />}
                        <h3>{statusHeader}</h3>
                        <p>{paymentMessage}</p>
                        {paymentStatus === 'success' && user?.email && (
                            <p className="payment-email-note">
                                {t('payment.emailSentTo', { email: user.email })}
                            </p>
                        )}
                        {paymentStatus === 'error' && (
                            <button className="secondary-btn" onClick={() => setPaymentStatus(null)}>
                                {t('payment.status.tryAgain')}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentModal;
